import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, PlayCircle, Film, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

/** Configurações → Vídeos de ajuda: qual vídeo da Biblioteca abre em cada "primeira vez" do aluno. */
export default function HelpVideos() {
  const [d, setD] = useState(null);
  const [vids, setVids] = useState([]);
  const [watch, setWatch] = useState(null);
  const load = () => Promise.all([api.get("/admin/help-videos"), api.get("/admin/videos")])
    .then(([h, v]) => { setD(h.data); setVids(v.data.videos.filter((x) => !["doc", "image"].includes(x.kind))); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const set = async (key, video_id) => {
    try { setD((await api.put("/admin/help-videos", { key, video_id })).data); toast.success(video_id ? "Vídeo ligado." : "Vídeo desligado."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const play = async (id) => {
    setWatch({ url: "" });
    try { setWatch({ url: (await api.get(`/admin/videos/${id}/preview`)).data.url }); } catch (e) { setWatch(null); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!d) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#232D4F]" /></div>;
  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Vídeos de ajuda</h1>
        <p className="text-slate-500 mt-1 max-w-3xl">Em cada "primeira vez" do aluno, o vídeo escolhido abre sozinho numa janela e fecha quando termina. Depois fica o botão "Ver vídeo" na tela para rever. Sem vídeo escolhido, nada aparece. Os vídeos vêm da <Link to="/admin/biblioteca" className="text-[#1E9E2A] font-semibold hover:underline">Biblioteca</Link>.</p>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
        {d.moments.map((m) => (
          <div key={m.key} className="p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid={`help-row-${m.key}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 font-semibold text-slate-800">
                {m.video_id ? <CheckCircle2 className="h-4 w-4 text-[#1E9E2A]" /> : <Circle className="h-4 w-4 text-slate-300" />} {m.label}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 ml-6">{m.where}</p>
            </div>
            <select value={m.video_id} onChange={(e) => set(m.key, e.target.value)} className="h-10 w-full sm:w-80 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">— Sem vídeo —</option>
              {vids.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <Button variant="outline" size="sm" disabled={!m.video_id} onClick={() => play(m.video_id)}><PlayCircle className="h-4 w-4 mr-1.5" /> Assistir</Button>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 flex items-center gap-1.5"><Film className="h-3.5 w-3.5" /> Dica: vídeos curtos (1 a 2 minutos), em pé (formato de celular), mostrando a tela real.</p>
      <Dialog open={!!watch} onOpenChange={(o) => !o && setWatch(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Vídeo de ajuda</DialogTitle></DialogHeader>
          {watch?.url ? <video src={watch.url} controls autoPlay controlsList="nodownload" className="w-full max-h-[70vh] rounded-lg bg-black" />
            : <div className="aspect-video grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
